import { IgCoreTextColors } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";

interface UploadedFilesCardProps {
  filenames: string[];
}

/**
 * The Figma "Survey summary" card (Rule Building Agent › node 15942:12969) as
 * it is used for a user's file attachment: a bordered summary row whose label
 * states what happened ("File uploaded") and whose answer rows list the files
 * by name.
 *
 * Layout is the Figma spec verbatim (theme spacing unit is 4px): a 4px-radius
 * card with a 1px `border/core/disabled` outline padded 16px all round, rows
 * separated by 16px, and inside a row the label (Body 1/Semi Bold, text
 * primary) sits 4px above its answer rows (Body 1/Regular, text secondary).
 * Long names wrap rather than overflow the card.
 */
export const UploadedFilesCard = ({ filenames }: UploadedFilesCardProps) => {
  const theme = useTheme();

  if (filenames.length === 0) {
    return null;
  }

  return (
    <Stack
      data-testid="uploaded-files-card"
      sx={{
        border: `1px solid ${theme.igBorders.core.disabled}`,
        borderRadius: "4px",
        gap: 4,
        overflow: "hidden",
        p: 4,
        width: "100%",
      }}
    >
      <Stack sx={{ gap: 1 }}>
        <IgTypography
          variant="body1"
          sx={{
            color: IgCoreTextColors.primary,
            fontWeight: 600,
            lineHeight: 1.5,
            overflowWrap: "anywhere",
          }}
        >
          {filenames.length === 1 ? "File uploaded" : "Files uploaded"}
        </IgTypography>
        {filenames.map((filename, index) => (
          <IgTypography
            // Two attachments can legitimately share a name, so the position
            // in the turn is what identifies a row.
            key={`${index}-${filename}`}
            variant="body1"
            sx={{
              color: IgCoreTextColors.secondary,
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {filename}
          </IgTypography>
        ))}
      </Stack>
    </Stack>
  );
};
